import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { Calendar, TrendingUp, DollarSign, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { getExpenseAnalytics } from '../../../api/expenses';
import { getBudgetVsActual } from '../../../api/budgets';
import { formatDateToISO } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const WholesaleExpenseAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [budgetVsActual, setBudgetVsActual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: formatDateToISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    end_date: formatDateToISO(new Date())
  });

  // Wholesale theme colors
  const COLORS = ['#047857', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#D1FAE5', '#F0FDF4'];

  useEffect(() => {
    fetchAnalytics();
    fetchBudgetVsActual();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await getExpenseAnalytics({ ...dateRange, module: 'wholesale' });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load wholesale expense analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetVsActual = async () => {
    try {
      const response = await getBudgetVsActual({ ...dateRange, module: 'wholesale' });
      setBudgetVsActual(response.data || []);
    } catch (error) {
      console.error('Failed to load wholesale budget comparison:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000000) {
      return `₦${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `₦${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  const calculateTotalExpenses = () => {
    if (!analytics?.by_status) return 0;
    return analytics.by_status.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
  };

  const calculateApprovedExpenses = () => {
    if (!analytics?.by_status) return 0;
    const approved = analytics.by_status.find(
      (item) => item.status === 'approved' || item.status === 'posted'
    );
    return approved ? parseFloat(approved.total_amount || 0) : 0;
  };

  const calculatePendingExpenses = () => {
    if (!analytics?.by_status) return 0;
    const pending = analytics.by_status.find((item) => item.status === 'pending');
    return pending ? parseFloat(pending.total_amount || 0) : 0;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wholesale Expense Analytics</h1>
        <p className="text-gray-600 mt-1">Comprehensive insights for wholesale module expenses</p>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-emerald-700">
            <Calendar size={20} />
            <span className="text-sm font-medium">Date Range:</span>
          </div>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
          />
          <span className="text-gray-400 font-bold">→</span>
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                Total Wholesale Expenses
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(calculateTotalExpenses())}
              </p>
            </div>
            <div className="bg-emerald-100 p-3 rounded-full text-emerald-700">
              <DollarSign size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                Approved & Posted
              </p>
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(calculateApprovedExpenses())}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full text-blue-600">
              <TrendingUp size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                Awaiting Approval
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(calculatePendingExpenses())}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full text-orange-600">
              <FileText size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                Total Submission Count
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics?.by_status?.reduce((sum, item) => sum + parseInt(item.count || 0), 0) ||
                  0}
              </p>
            </div>
            <div className="bg-gray-100 p-3 rounded-full text-gray-600">
              <FileText size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-50 pb-2">
            Category Allocation
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics?.by_category || []}
                dataKey="total_approved"
                nameKey="category_name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                label={(entry) => entry.category_name}
              >
                {(analytics?.by_category || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-50 pb-2">
            Status Distribution
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics?.by_status || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="status" />
              <YAxis tickFormatter={formatCompactCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar
                dataKey="total_amount"
                fill="var(--wholesale-primary, #047857)"
                name="Amount"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-50 pb-2">
            Spending Trend (Monthly)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics?.monthly_trend || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCompactCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="total_amount"
                stroke="var(--wholesale-primary, #047857)"
                strokeWidth={3}
                dot={{
                  r: 6,
                  fill: '#fff',
                  stroke: 'var(--wholesale-primary, #047857)',
                  strokeWidth: 2
                }}
                name="Total Wholesale Expenses"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-50 pb-2">
            Wholesale Budget vs Actual
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={budgetVsActual}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="category_name" angle={-45} textAnchor="end" height={100} />
              <YAxis tickFormatter={formatCompactCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="allocated_amount" fill="#94a3b8" name="Budget" radius={[4, 4, 0, 0]} />
              <Bar
                dataKey="spent_amount"
                fill="var(--wholesale-primary, #047857)"
                name="Actual"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Breakdown Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Wholesale Category Breakdown</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left font-medium tracking-wider">Category</th>
                <th className="px-6 py-3 text-right font-medium tracking-wider">Count</th>
                <th className="px-6 py-3 text-right font-medium tracking-wider">Total Amount</th>
                <th className="px-6 py-3 text-right font-medium tracking-wider">% of Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 text-sm">
              {(analytics?.by_category || []).map((category, index) => {
                const totalExpenses = analytics.by_category.reduce(
                  (sum, cat) => sum + parseFloat(cat.total_approved || 0),
                  0
                );
                const percentage =
                  totalExpenses > 0
                    ? (parseFloat(category.total_approved) / totalExpenses) * 100
                    : 0;

                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {category.category_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                      {category.count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-emerald-700">
                      {formatCurrency(category.total_approved)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-gray-600">{percentage.toFixed(1)}%</span>
                        <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-emerald-500 h-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WholesaleExpenseAnalyticsPage;
